import { apiSlice } from './apiSlice'

export interface Memorial {
  id: string
  case_id: string
  team_id: string
  file_url: string
  file_name: string
  file_size: number
  title?: string
  version?: number
  submitted_at?: string
  status: string
  storage_path?: string
  tags?: string[]
  force_submitted?: boolean
  force_submitted_by?: string
  force_submitted_reason?: string
  submission_locked?: boolean
  is_official_submission?: boolean
  /** Legacy: vai trò bảo vệ — 'plaintiff' (Nguyên đơn) | 'defendant' (Bị đơn) */
  representing_role?: 'plaintiff' | 'defendant' | null
  /** Flow `dual_claimant_packets`: side của packet — 'scenario_a' | 'scenario_b' */
  r1_side?: 'scenario_a' | 'scenario_b' | null
  /** Q1 audit (2026-05-21): alias FE-friendly của r1_side — 1 = scenario_a, 2 = scenario_b */
  submission_index?: 1 | 2 | null
}

export interface MemorialVersionSummary {
  memorial_id: string
  case_id: string
  team_id: string
  version: number
  title?: string
  file_name: string
  file_size: number
  submitted_at?: string
  status: string
  file_url?: string
  is_official_submission: boolean
  submission_index?: 1 | 2 | null
}

export interface MemorialDeadlineStatus {
  case_id: string
  submission_open: boolean
  message: string
  case_deadline?: string
  round_memorial_deadline?: string
  round_id?: string
  round_opens_at?: string
  round_closes_at?: string
  server_now_iso?: string
  timezone?: string
}

export interface MemorialSubmissionLockState {
  locked: boolean
  case_id: string
  team_id: string
  official_memorial_id?: string
  locked_at?: string
  locked_by_user_id?: string
  r1_side?: 'scenario_a' | 'scenario_b' | null
}

export interface MemorialCreate {
  case_id: string
  team_id: string
  file: File
  title?: string
  version?: number
}

export const memorialsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getMemorials: builder.query<
      Memorial[],
      { case_id?: string; team_id?: string; r1_side?: 'scenario_a' | 'scenario_b' }
    >({
      query: (params) => ({
        url: '/memorials/list',
        params,
      }),
      providesTags: ['Memorial'],
    }),
    getMemorial: builder.query<Memorial, string>({
      query: (id) => `/memorials/${id}`,
      providesTags: (result, error, id) => [{ type: 'Memorial', id }],
    }),
    getMemorialVersions: builder.query<
      MemorialVersionSummary[],
      { case_id: string; team_id: string }
    >({
      query: (params) => ({
        url: '/memorials/versions',
        params,
      }),
      providesTags: ['Memorial'],
    }),
    getMemorialDeadlineStatus: builder.query<MemorialDeadlineStatus, string>({
      query: (case_id) => ({
        url: '/memorials/deadline-status',
        params: { case_id },
      }),
    }),
    getMemorialLockStatus: builder.query<
      MemorialSubmissionLockState,
      { case_id: string; team_id: string; r1_side?: 'scenario_a' | 'scenario_b' }
    >({
      query: (params) => ({
        url: '/memorials/lock-status',
        params,
      }),
      providesTags: ['Memorial'],
    }),
    submitMemorial: builder.mutation<Memorial, FormData>({
      query: (formData) => ({
        url: '/memorials/create',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Memorial'],
    }),
    officialSubmitMemorial: builder.mutation<MemorialSubmissionLockState, string>({
      query: (memorialId) => ({
        url: `/memorials/${memorialId}/official-submit`,
        method: 'POST',
      }),
      invalidatesTags: ['Memorial'],
    }),
    releaseMemorialSubmissionLock: builder.mutation<
      { message: string },
      {
        case_id: string
        team_id: string
        reason?: string
        r1_side?: 'scenario_a' | 'scenario_b'
      }
    >({
      query: ({ case_id, team_id, reason, r1_side }) => ({
        url: '/memorials/admin/submission-lock',
        method: 'DELETE',
        params: {
          case_id,
          team_id,
          ...(reason ? { reason } : {}),
          ...(r1_side ? { r1_side } : {}),
        },
      }),
      invalidatesTags: ['Memorial'],
    }),
    forceSubmitMemorial: builder.mutation<
      MemorialSubmissionLockState,
      { memorialId: string; reason: string }
    >({
      query: ({ memorialId, reason }) => ({
        url: `/memorials/${memorialId}/force-submit`,
        method: 'POST',
        params: { reason },
      }),
      invalidatesTags: ['Memorial'],
    }),
    downloadMemorial: builder.query<Blob, string>({
      query: (id) => ({
        url: `/memorials/${id}/download`,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    downloadMemorialAsPdf: builder.query<Blob, string>({
      query: (id) => ({
        url: `/memorials/${id}/download?as_pdf=1`,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    deleteMemorial: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/memorials/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Memorial'],
    }),
  }),
})

export const {
  useGetMemorialsQuery,
  useGetMemorialQuery,
  useGetMemorialVersionsQuery,
  useLazyGetMemorialVersionsQuery,
  useGetMemorialDeadlineStatusQuery,
  useGetMemorialLockStatusQuery,
  useSubmitMemorialMutation,
  useOfficialSubmitMemorialMutation,
  useReleaseMemorialSubmissionLockMutation,
  useForceSubmitMemorialMutation,
  useLazyDownloadMemorialQuery,
  useLazyDownloadMemorialAsPdfQuery,
  useDeleteMemorialMutation,
} = memorialsApi
